import {
  Paper,
  Avatar,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { usePasswordResetRequest } from "../../Query/useAuth";
import colors from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import Loading from "../Common/Loading";
import { ComoInput } from "../Common/ComoInput";

const PasswordResetRequest = () => {
  const [email, setEmail] = useState("");
  const { mutate: passwordResetMutate, isPending: isLoading } =
    usePasswordResetRequest();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handlePasswordResetRequest = (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault();
    passwordResetMutate(email, {
      onSuccess: () => setIsSubmitted(true),
    });
  };

  const handleNavigateToLogin = () => {
    navigate("/login");
  };

  if (isLoading) {
    return (
      <Loading
        backgroundColor={colors.gray100}
        color1={colors.accent}
        color2={colors.primary_dark}
      />
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        minHeight: "100vh",
        overflow: "hidden",
        backgroundColor: colors.gray50,
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: 3,
          py: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${colors.primary_dark}`,
            backgroundColor: colors.white,
            maxWidth: { xs: "100%", sm: 480 },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Avatar
              sx={{
                width: 72,
                height: 72,
                backgroundColor: colors.white,
                marginBottom: 2,
                border: `2px solid ${colors.primary_dark}`,
              }}
            >
              <img
                src="/commo_logo.png"
                alt="Logo"
                style={{ width: "100%", height: "auto" }}
              />
            </Avatar>
            <Typography
              variant="h4"
              sx={{
                color: colors.primary_dark,
                fontWeight: 600,
                textAlign: "center",
                mb: 1,
              }}
            >
              Connected Innovation Hub
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.gray600, textAlign: "center" }}
            >
              Enter your email to receive password reset instructions.
            </Typography>
          </Box>

          <form onSubmit={handlePasswordResetRequest}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {!isSubmitted ? (
                <>
                  <ComoInput
                    label="Email"
                    required
                    value={email}
                    onChange={handleChangeEmail}
                    disabled={isLoading}
                    primaryColor={colors.primary_dark}
                    multiline={false}
                    maxRows={1}
                    size="medium"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: colors.gray50,
                        "&.Mui-focused": {
                          backgroundColor: colors.white,
                        },
                        "&.Mui-disabled": {
                          backgroundColor: colors.gray100,
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: `${colors.primary_dark} !important`,
                        "&::placeholder": {
                          color: `${colors.gray400} !important`,
                          opacity: 1,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: `${colors.gray600} !important`,
                        "&.Mui-focused": {
                          color: `${colors.primary_dark} !important`,
                        },
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    variant="modern"
                    fullWidth
                    disabled={isLoading}
                    sx={{
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 600,
                      borderRadius: 1.5,
                      mt: 1,
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress
                        size={24}
                        sx={{ color: colors.white }}
                      />
                    ) : (
                      "Send Password Reset Request"
                    )}
                  </Button>
                </>
              ) : (
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: colors.primary_dark,
                    marginBottom: "20px",
                    textAlign: "center",
                  }}
                >
                  Password reset request sent! Check your email.
                </Typography>
              )}

              <Button
                variant="outlined"
                fullWidth
                disabled={isLoading}
                onClick={handleNavigateToLogin}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  borderColor: colors.gray200,
                  color: colors.primary_dark,
                  backgroundColor: colors.white,
                  "&:hover": {
                    borderColor: colors.primary_dark,
                    backgroundColor: colors.gray50,
                  },
                }}
              >
                Go back to login
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      <Box
        sx={{
          position: "fixed",
          left: 20,
          bottom: 20,
          zIndex: 1100,
          display: { xs: "none", md: "block" },
        }}
      >
        <img
          src="/logo_big_blue.png"
          alt="Commo Logo"
          style={{ maxHeight: "50px" }}
        />
      </Box>
    </Box>
  );
};

export default PasswordResetRequest;
